import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Users, FileText, FolderKanban, DollarSign, AlertCircle, CalendarClock, Mail } from "lucide-react";

const STATUS_LABEL = {
  planning: { es: "Planificación", en: "Planning" },
  in_progress: { es: "En progreso", en: "In progress" },
  completed: { es: "Completado", en: "Completed" },
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-primary"}`} />
      </div>
      <p className="mt-3 font-head text-3xl font-extrabold text-secondary">{value}</p>
    </div>
  );
}

export default function Overview() {
  const { t, lang } = useLang();
  const [biz, setBiz] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reminders, setReminders] = useState({ upcoming: [], overdue: [] });

  useEffect(() => {
    api.get("/businesses/mine").then((r) => setBiz(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/projects/reminders").then((r) => setReminders(r.data)).catch(() => {});
  }, []);

  const revenue = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/projects/reminders/send");
      if (data.sent) toast.success(lang === "es" ? `Recordatorios enviados (${data.count})` : `Reminders sent (${data.count})`);
      else toast.info(lang === "es" ? "Email no configurado aún (RESEND_API_KEY)." : "Email not configured yet (RESEND_API_KEY).");
    } catch {
      toast.error(lang === "es" ? "No se pudieron enviar" : "Could not send");
    }
  };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_overview")}</h1>

      {biz && !biz.published && (
        <Link to="/dashboard/subscription" className="mt-4 flex items-center gap-3 rounded-sm border border-primary/40 bg-primary/10 p-4 text-sm text-orange-800" data-testid="publish-warning">
          <AlertCircle className="h-5 w-5 text-primary" />
          Tu negocio aún no está publicado. Suscríbete a un plan para aparecer en el directorio.
        </Link>
      )}
      {!biz && (
        <Link to="/dashboard/business" className="mt-4 flex items-center gap-3 rounded-sm border border-primary/40 bg-primary/10 p-4 text-sm text-orange-800" data-testid="create-business-warning">
          <AlertCircle className="h-5 w-5 text-primary" />
          Crea el perfil de tu negocio para empezar.
        </Link>
      )}

      {(reminders.overdue.length > 0 || reminders.upcoming.length > 0) && (
        <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5" data-testid="reminders-card">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><CalendarClock className="h-5 w-5 text-primary" /> {lang === "es" ? "Recordatorios de entrega" : "Delivery reminders"}</h2>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={sendReminders} data-testid="send-reminders-btn"><Mail className="mr-1 h-4 w-4" />{lang === "es" ? "Enviar por email" : "Send by email"}</Button>
          </div>
          <div className="mt-3 space-y-2">
            {reminders.overdue.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-sm border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm" data-testid={`reminder-overdue-${p.id}`}>
                <span className="font-semibold text-secondary">{p.title} {p.client_name ? `· ${p.client_name}` : ""}</span>
                <span className="font-bold text-destructive">{lang === "es" ? `Vencido hace ${Math.abs(p.days_left)} día(s)` : `${Math.abs(p.days_left)} day(s) overdue`}</span>
              </div>
            ))}
            {reminders.upcoming.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-sm border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid={`reminder-upcoming-${p.id}`}>
                <span className="font-semibold text-secondary">{p.title} {p.client_name ? `· ${p.client_name}` : ""}</span>
                <span className="font-bold text-primary">{p.days_left === 0 ? (lang === "es" ? "Entrega hoy" : "Due today") : (lang === "es" ? `En ${p.days_left} día(s)` : `In ${p.days_left} day(s)`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label={t("dash_clients")} value={clients.length} />
        <Stat icon={FileText} label={t("dash_invoices")} value={invoices.length} />
        <Stat icon={FolderKanban} label={t("dash_projects")} value={projects.length} />
        <Stat icon={DollarSign} label="Ingresos" value={`$${revenue.toFixed(2)}`} accent="text-accent" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">{t("dash_invoices")}</h2>
          <div className="mt-4 space-y-2">
            {invoices.slice(0, 5).map((i) => (
              <div key={i.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                <span className="font-semibold text-slate-700">{i.number} · {i.client_name}</span>
                <span className={`font-bold ${i.status === "paid" ? "text-accent" : "text-primary"}`}>${i.total.toFixed(2)}</span>
              </div>
            ))}
            {invoices.length === 0 && <p className="py-4 text-sm text-slate-400">—</p>}
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-6">
          <h2 className="font-head text-lg font-bold text-secondary">{t("dash_projects")}</h2>
          <div className="mt-4 space-y-2">
            {projects.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                <span className="font-semibold text-slate-700">{p.title}</span>
                <span className="rounded-sm bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase text-slate-500">{STATUS_LABEL[p.status] ? (lang === "es" ? STATUS_LABEL[p.status].es : STATUS_LABEL[p.status].en) : p.status}</span>
              </div>
            ))}
            {projects.length === 0 && <p className="py-4 text-sm text-slate-400">—</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
